import {Button} from 'antd'
import {getId, getType} from 'apis/model/base'
import classNames from 'classnames'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useState} from 'react'
import {IoHeart, IoHeartOutline} from 'react-icons/io5'
import {RxEnter} from 'react-icons/rx'
import {useJoin, useVote} from 'views/Server/ServerContainer'
import {useFollow} from 'views/Server/useFollow'
import {NullAsDefault, renderOwnChild} from 'views/Shared'
import {RiHeartLine, RiRssFill} from "react-icons/ri";
import {ifThenElseMultipleConditions} from "../../../helpers/ifThenElseMultipleConditions";

const ToggleActionButton = React.forwardRef(
    (
        {
            isToggle,
            active = {
                label: 'active',
                icon: <IoHeart/>,
                color: 'default',
                variant: 'filled',
                className: 'rounded-lg no-border text-color-400',
                params: {},
            },
            inactive = {
                label: 'inactive',
                icon: <IoHeartOutline/>,
                color: 'primary',
                variant: 'solid',
                className: 'rounded-lg no-border',
                params: {},
            },
            onClick = NullAsDefault,
            useDebounce = true,
            ...props
        },
        ref
    ) => {
        const t = useTranslate()

        const [isLoading, setLoading] = useState(false)

        const debounce = _.debounce(() => {
            onClick()
            setLoading(false)
        }, 200)

        const {icon, label, color, variant, className, params} = isToggle ? active : inactive

        return (
            <Button
                ref={ref}
                block={true}
                icon={icon}
                color={color} variant={variant}
                disabled={isLoading}
                className={classNames("flex flex-center gap-2", className)}
                {...params}
                {...props}
                onClick={
                    useDebounce
                        ? () => {
                            setLoading(true)
                            debounce()
                        }
                        : onClick
                }>
                {label ? t(label) : null}
            </Button>
        )
    }
)

export const UseVoteHandler = ({children, ...props}) => {
    const action = useVote({...props}) || NullAsDefault
    return _.isFunction(children) ? children(action) : children
}

export const UseFollowHandler = ({children, ...props}) => {
    const action = useFollow({...props}) || NullAsDefault
    return _.isFunction(children) ? children(action) : children
}

export const VoteButton = ({
                               item,
                               owner_id: id,
                               owner_type: type,
                               vote_status: status,
                               Wrapper = renderOwnChild,
                               Component = ToggleActionButton,
                           }) => {
    const owner_id = getId(item) || id
    const owner_type = getType(item) || type
    const vote_status = _.get(item, 'vote_status') || status

    if (_.isEmpty(owner_id) || _.isEmpty(owner_type)) {
        return null
    }
    return (
        <UseVoteHandler
            {...{
                owner_id,
                owner_type,
                vote_status,
            }}>
            {(handleVote = NullAsDefault) => (
                <Wrapper>
                    <Component
                        isToggle={vote_status}
                        active={{
                            label: 'liked',
                            color: 'default',
                            variant: 'filled',
                            className: 'no-border text-color-300 hover:text-color-100 italic'
                        }}
                        inactive={{
                            label: 'like',
                            icon: <RiHeartLine/>,
                            color: 'primary',
                            variant: 'solid',
                            className: 'font-medium no-border'
                        }}
                        onClick={() => handleVote()}
                    />
                </Wrapper>
            )}
        </UseVoteHandler>
    )
}

export const FollowButton = ({
                                 item,
                                 owner_id: id,
                                 owner_type: type,
                                 follow_status: status,
                                 // secondary: hạ cấp style nút Follow khi chưa follow (thành outline) - dùng khi
                                 // đứng cạnh JoinButton (VD OrganizationBanner): join đã bao hàm follow, nên ưu
                                 // tiên hiển thị Join là primary, Follow lùi thành secondary, học theo Twitter/X.
                                 // Không ảnh hưởng trạng thái "đang follow" (active) - vẫn giữ style muted như cũ.
                                 secondary = false,
                                 Wrapper = renderOwnChild,
                                 Component = ToggleActionButton,
                             }) => {
    const owner_id = getId(item) || id
    const owner_type = getType(item) || type
    const follow_status = _.get(item, 'follow_status') || status

    if (_.isEmpty(owner_id) || _.isEmpty(owner_type)) {
        return null
    }
    return (
        <Wrapper>
            <UseFollowHandler
                {...{
                    owner_id,
                    owner_type,
                    follow_status,
                }}>
                {(handleFollow = NullAsDefault) => (
                    <Component
                        isToggle={follow_status}
                        active={{
                            label: 'following',
                            color: 'default',
                            variant: 'filled',
                            className: 'no-border rounded-lg text-color-300 hover:text-color-100 italic'
                        }}
                        inactive={secondary ? {
                            label: 'follow',
                            icon: <RiRssFill/>,
                            color: 'primary',
                            variant: 'outlined',
                            className: 'font-medium rounded-lg'
                        } : {
                            label: 'follow',
                            icon: <RiRssFill/>,
                            color: 'primary',
                            variant: 'solid',
                            className: 'font-medium no-border rounded-lg'
                        }}
                        onClick={() => handleFollow()}
                    />
                )}
            </UseFollowHandler>
        </Wrapper>
    )
}

export const JoinButton = ({
                               id,
                               type,
                               item,
                               style,
                               className,
                               icon = <RxEnter/>,
                               ...props
                           }) => {
    const t = useTranslate()

    const {joined_status, join_status} = item

    const params = item
        ? {
            id,
            type,
            status: joined_status,
            join_status
        }
        : {}
    const handleJoin = useJoin(params)
    return (
        <Button
            icon={item?.join_status === 1 ? <RxEnter/> : null}
            variant={item?.join_status === 1 ? 'filled' : 'solid'}
            color={item?.join_status === 1 ? 'default' : 'primary'}
            style={style}
            disabled={!!item?.join_status && item?.join_status !== 1}
            onClick={() => {
                handleJoin()
            }}
            className={classNames(
                'font-medium no-border rounded-lg px-3 gap-1',
                className
            )}
            {...props}>
            {ifThenElseMultipleConditions(
                t('join'),
                join_status === -1, t('pending'),
                join_status === 1, t('leave'),
                join_status === -7, t('banned'),
                join_status === -8, t('kicked'),
                join_status === -9, t('rejected')
            )}
        </Button>
    )
}
/// -1: pending
/// -7: banned
/// -8: kicked
/// -9: deny
/// 1: normal, approved -> normal
/// 0: deleted

export default ToggleActionButton
